/**
 * Motion vocabulary lifted from the reference build.
 *
 * Every value here was read out of the reference's own appear-animation
 * config rather than eyeballed, so sections share one authored feel instead of
 * each one inventing its own curve. The four cubic-beziers below are the exact
 * ones the reference ships; the springs are its spring configs converted to
 * tweens, since GSAP tweens on duration rather than on physics.
 */

/** The reference's four authored easing curves. */
export const ease = {
    /** The long image flights. Very slow out of the gate, then decisive. */
    flight: 'cubic-bezier(0.68, 0, 0.05, 0.99)',
    /** Slight anticipation then overshoot. Used on scale-ins. */
    overshoot: 'cubic-bezier(0.96, -0.02, 0.38, 1.01)',
    /** The wordmark. Holds longer than flight before releasing. */
    word: 'cubic-bezier(0.68, 0, 0, 0.99)',
    /** Copy dropping in from above. */
    drop: 'cubic-bezier(0.68, 0, 0.16, 0.97)'
} as const

/**
 * Spring configs converted to duration + easing.
 *
 * For each, the undamped frequency is sqrt(stiffness / mass) and the damping
 * ratio is damping / (2 * sqrt(stiffness * mass)). Every spring the reference
 * uses is critically damped or overdamped except `flip`, so they settle without
 * bouncing and a power ease out is a faithful stand-in. The duration is the
 * settling time those constants produce.
 */
export const spring = {
    /** stiffness 125, damping 27, mass 0.3. Eyebrow and small copy. */
    copy: { duration: 0.9, ease: 'power3.out' },
    /** stiffness 100, damping 17, mass 0.3. Header wordmark. */
    brand: { duration: 0.95, ease: 'power3.out' },
    /** stiffness 80, damping 17, mass 0.3. Header metadata. */
    meta: { duration: 1, ease: 'power3.out' },
    /** stiffness 70, damping 27, mass 0.3. The column rules. */
    grid: { duration: 1.3, ease: 'power2.out' },
    /** stiffness 235, damping 30, mass 1. Tight, late badge. */
    badge: { duration: 0.85, ease: 'power3.out' },
    /** stiffness 153, damping 21, mass 1. Underdamped, so it overshoots. */
    flip: { duration: 1.2, ease: 'back.out(1.1)' },
    /** stiffness 110, damping 35, mass 1. */
    settle: { duration: 1.1, ease: 'power2.out' }
} as const

/**
 * The hero load timeline, exactly as the reference sequences it.
 *
 * Delays are absolute from page load, which is what makes the entrance overlap
 * the way it does: the side photographs are still travelling when the main one
 * starts, and all three land within a few hundred milliseconds of the wordmark.
 */
export const heroLoad = {
    imgRight: { from: { y: 1200, rotate: -6 }, delay: 0, duration: 3.6, ease: ease.flight },
    imgLeft: { from: { y: 1261, rotate: 11 }, delay: 0.4, duration: 3.6, ease: ease.flight },
    imgMain: { from: { y: 1060, rotate: -6 }, delay: 1.2, duration: 3, ease: ease.flight },
    /** The main photograph's own crop, inside its flying wrapper. */
    imgMainInner: {
        from: { y: -100, scale: 0.4, rotate: 5 },
        delay: 2.8,
        duration: 1.4,
        ease: ease.overshoot
    },
    word: { from: { y: 190, scale: 0.6 }, delay: 3, duration: 2, ease: ease.word },
    grid: { from: { y: 80 }, delay: 3.4, ...spring.grid },
    gradient: { from: { y: 150 }, delay: 3.6, ...spring.copy },
    caption: { from: { y: -100 }, delay: 3.6, duration: 1.1, ease: ease.drop },
    eyebrow: { from: { y: 60 }, delay: 4, ...spring.copy },
    cue: { from: { rotateY: 270 }, delay: 4.2, ...spring.flip },
    /** Header rail. Starts immediately, ahead of everything else. */
    brand: { from: { y: 100 }, delay: 0, ...spring.brand },
    rail: { from: { y: 100 }, delay: 0.1, ...spring.meta }
} as const

/** Resting rotations that stay applied after the entrance settles. */
export const heroRest = {
    left: -13,
    right: 11
} as const
